import {
  createCdfEntry,
  type RawCdfEntry,
} from 'features/dataLayers/customCdfs/cdfGenerator'
import type { TminSource } from 'features/dataLayers/generation/calculations/calculateDerivedValues'
import { getGeneratedCdfSignatureHash } from 'features/dataLayers/generation/generatedCdfSignature'
import { SHORT_TERM_FEATURE_ALGORITHM_VERSION } from 'features/dataLayers/generation/shortTermCorrosionRate/constants'
import { getShortTermFeatureRectanglesHash } from 'features/dataLayers/generation/shortTermCorrosionRate/rateAndRemainingLife'
import type { ShortTermRateGenerationSeries } from 'features/dataLayers/generation/shortTermCorrosionRate/shortTermRateGenerationSeries'
import type { ShortTermCorrosionFeatureRectangle } from 'features/dataLayers/generation/shortTermCorrosionRate/types'
import {
  type CalculateShortTermRemainingLifeCdfProps,
  type GenerateShortTermCorrosionRateCdfProps,
  type ShortTermCorrosionRateGenerationParams,
  ShortTermTminSource,
} from 'features/dataLayers/generation/shortTermCorrosionRate/useGenerateShortTermCorrosionRateCDFs'
import {
  CacheableRolodexEntryJson,
  GeckoComponentDataFrame,
  GeckoMeasurementUnit,
  GeckoSource,
  GeckoView,
} from 'types'

export function getGeneratedShortTermRateCdfId(
  series: ShortTermRateGenerationSeries,
  params: ShortTermCorrosionRateGenerationParams,
  featureRectangles: readonly ShortTermCorrosionFeatureRectangle[] = []
): string {
  const signatureHash = getGeneratedCdfSignatureHash([
    'short-term-corrosion-rate',
    SHORT_TERM_FEATURE_ALGORITHM_VERSION,
    series.targetCdf.id,
    series.targetCdf.generation,
    series.comparisonCdf.id,
    series.comparisonCdf.generation,
    series.targetDifferenceFromNominalCdf.id,
    series.targetDifferenceFromNominalCdf.generation,
    getParamsSignature(params),
    getShortTermFeatureRectanglesHash(featureRectangles),
  ])

  return `short-term-corrosion-rate-${signatureHash}`
}

export function getGeneratedShortTermRemainingLifeCdfId({
  targetCdf,
  shortTermRateCdf,
  tminSource,
  forecastComponentConfigGeneration,
}: {
  targetCdf: GeckoComponentDataFrame
  shortTermRateCdf: GeckoComponentDataFrame
  tminSource: TminSource
  forecastComponentConfigGeneration?: number
}): string {
  const signatureHash = getGeneratedCdfSignatureHash([
    'short-term-remaining-life',
    SHORT_TERM_FEATURE_ALGORITHM_VERSION,
    targetCdf.id,
    targetCdf.generation,
    shortTermRateCdf.id,
    shortTermRateCdf.generation,
    ...getTminSourceSignatureParts(tminSource),
    forecastComponentConfigGeneration ?? 1,
  ])

  return `short-term-remaining-life-${signatureHash}`
}

export function getShortTermTminSource(
  tminSource: TminSource
): ShortTermTminSource {
  return tminSource.kind === 'manual'
    ? ShortTermTminSource.ManualOverride
    : ShortTermTminSource.Recommended
}

export function assembleGeneratedShortTermRateCdf({
  targetCdf,
  targetInspection,
  comparisonCdf,
  comparisonInspection,
  targetDifferenceFromNominalCdf,
  params,
  featureRectangles = [],
}: GenerateShortTermCorrosionRateCdfProps & {
  params: ShortTermCorrosionRateGenerationParams
  featureRectangles?: readonly ShortTermCorrosionFeatureRectangle[]
}) {
  const thisId = getGeneratedShortTermRateCdfId(
    {
      targetCdf,
      targetInspection,
      comparisonCdf,
      comparisonInspection,
      targetDifferenceFromNominalCdf,
    },
    params,
    featureRectangles
  )
  const thisGeneration = getGeneratedShortTermRateGeneration({
    targetCdf,
    targetInspection,
    comparisonCdf,
    comparisonInspection,
    targetDifferenceFromNominalCdf,
  })
  const cdfEntry: CacheableRolodexEntryJson = {
    isFromCache: true,
    ...createCdfEntry(
      thisId,
      {
        source: GeckoSource.Projection,
        aggregation: targetCdf.data.params.aggregation,
        anglePlotSetting: targetCdf.data.params.angle_plot_setting,
        algorithm: targetCdf.data.params.algorithm,
        binning: targetCdf.data.binning,
        geometry: targetCdf.data.geometry,
        tags: targetCdf.tags,
        view: GeckoView.ShortTermCorrosionRate,
        measurementUnit: GeckoMeasurementUnit.MillimetersPerYear,
        generation: thisGeneration,
        dynamicParams: getShortTermRateDynamicParams({
          targetCdf,
          targetInspection,
          comparisonCdf,
          comparisonInspection,
          targetDifferenceFromNominalCdf,
          params,
          featureRectangles,
        }),
      },
      targetCdf.raw.type as RawCdfEntry['type']
    ),
  }

  return GeckoComponentDataFrame.CreateFromJSON(cdfEntry)
}

export function assembleGeneratedShortTermRemainingLifeCdf({
  targetCdf,
  targetInspection,
  comparisonCdf,
  comparisonInspection,
  shortTermRateCdf,
  tminSource,
  forecastComponentConfigGeneration,
}: Omit<
  CalculateShortTermRemainingLifeCdfProps,
  'assetModel' | 'componentId' | 'forecastComponentConfig'
>) {
  const thisId = getGeneratedShortTermRemainingLifeCdfId({
    targetCdf,
    shortTermRateCdf,
    tminSource,
    forecastComponentConfigGeneration,
  })
  const thisGeneration = getGeneratedShortTermRemainingLifeGeneration({
    targetCdf,
    shortTermRateCdf,
    forecastComponentConfigGeneration,
  })
  const tminMethod =
    tminSource.kind === 'method' ? tminSource.method : undefined
  const cdfEntry: CacheableRolodexEntryJson = {
    isFromCache: true,
    ...createCdfEntry(
      thisId,
      {
        source: GeckoSource.Projection,
        aggregation: targetCdf.data.params.aggregation,
        anglePlotSetting: targetCdf.data.params.angle_plot_setting,
        algorithm: targetCdf.data.params.algorithm,
        binning: targetCdf.data.binning,
        geometry: targetCdf.data.geometry,
        tags: targetCdf.tags,
        view: GeckoView.ShortTermRemainingLife,
        measurementUnit: GeckoMeasurementUnit.Years,
        generation: thisGeneration,
        tminMethod,
        dynamicParams: {
          target_thickness_cdf_id: targetCdf.id,
          short_term_corrosion_rate_cdf_id: shortTermRateCdf.id,
          target_inspection_id: targetInspection.id,
          comparison_thickness_cdf_id: comparisonCdf.id,
          comparison_inspection_id: comparisonInspection.id,
          algorithm_version: SHORT_TERM_FEATURE_ALGORITHM_VERSION,
          tmin_source: getShortTermTminSource(tminSource),
          ...(tminMethod ? { tmin_method: tminMethod.id } : {}),
          ...(tminSource.kind === 'manual'
            ? {
                manual_tmin_value: tminSource.value.num,
                manual_tmin_unit: tminSource.value.unit.id,
              }
            : {}),
          forecast_component_config_generation:
            forecastComponentConfigGeneration ?? 1,
        },
      },
      targetCdf.raw.type as RawCdfEntry['type']
    ),
  }

  return GeckoComponentDataFrame.CreateFromJSON(cdfEntry)
}

function getTminSourceSignatureParts(
  tminSource: TminSource
): Array<string | number | undefined> {
  return tminSource.kind === 'manual'
    ? [tminSource.kind, tminSource.value.num, tminSource.value.unit.id]
    : [tminSource.kind, tminSource.method.id]
}

function getGeneratedShortTermRateGeneration(
  series: ShortTermRateGenerationSeries
): number {
  let generation = series.targetCdf.generation || 1

  for (const cdf of [
    series.comparisonCdf,
    series.targetDifferenceFromNominalCdf,
  ]) {
    generation *= cdf.generation || 1
  }

  return generation
}

function getGeneratedShortTermRemainingLifeGeneration({
  targetCdf,
  shortTermRateCdf,
  forecastComponentConfigGeneration,
}: {
  targetCdf: GeckoComponentDataFrame
  shortTermRateCdf: GeckoComponentDataFrame
  forecastComponentConfigGeneration?: number
}): number {
  return (
    (targetCdf.generation || 1) *
    (shortTermRateCdf.generation || 1) *
    (forecastComponentConfigGeneration || 1)
  )
}

function getShortTermRateDynamicParams({
  targetCdf,
  targetInspection,
  comparisonCdf,
  comparisonInspection,
  targetDifferenceFromNominalCdf,
  params,
  featureRectangles = [],
}: GenerateShortTermCorrosionRateCdfProps & {
  params: ShortTermCorrosionRateGenerationParams
  featureRectangles?: readonly ShortTermCorrosionFeatureRectangle[]
}) {
  return {
    target_thickness_cdf_id: targetCdf.id,
    comparison_thickness_cdf_id: comparisonCdf.id,
    target_difference_from_nominal_cdf_id: targetDifferenceFromNominalCdf.id,
    target_inspection_id: targetInspection.id,
    comparison_inspection_id: comparisonInspection.id,
    algorithm_version: SHORT_TERM_FEATURE_ALGORITHM_VERSION,
    feature_count: featureRectangles.length,
    feature_rectangles_hash:
      getShortTermFeatureRectanglesHash(featureRectangles),
    ...params,
  }
}

function getParamsSignature(
  params: ShortTermCorrosionRateGenerationParams
): string {
  return [
    params.target_feature_core_dfn_percent,
    params.target_feature_grow_dfn_percent,
    params.min_feature_area_feet_squared,
    params.target_feature_merge_distance_feet,
    params.target_feature_max_rect_area_feet_squared,
    params.target_feature_rect_padding_feet,
    params.prior_support_search_radius_feet,
    params.prior_support_translation_step_bins,
    params.prior_support_distance_weight,
    params.prior_support_min_coverage_fraction,
    params.prior_support_missing_coverage_weight,
    params.prior_support_min_value_count,
  ]
    .map((value) => String(value).replace(/[^a-zA-Z0-9]+/g, '_'))
    .join('-')
}
